import React from "react"
import { Button, Container, FlashbarProps, Modal, ModalProps, SpaceBetween, Tiles, TilesProps, Alert, Badge, Box, ColumnLayout, Header, Spinner } from "@cloudscape-design/components"
import { MatchupPrediction, getPredictionForMatchup } from "../../common"

interface PoolModalProps extends ModalProps {
    week: string
    teams: Record<string, any>
    setVisible: React.Dispatch<React.SetStateAction<boolean>>
    setNotifications: React.Dispatch<React.SetStateAction<FlashbarProps.MessageDefinition[]>>
}

export const PoolModal = ({ week, teams, visible, setVisible }: PoolModalProps) => {
    const date = new Date()
    const year = date.getMonth() < 3 ? date.getFullYear() - 1 :date.getFullYear()
    const [loaded, setLoaded] = React.useState(false);
    const [selectedItems, setSelectedItems] = React.useState<Array<string>>([]);
    const [items, setItems] = React.useState<TilesProps.TilesDefinition[][]>([]);
    const [gameData, setGameData] = React.useState<Array<any>>([]);
    const [predictions, setPredictions] = React.useState<Record<number, MatchupPrediction | null>>({});
    const [loadingPredictions, setLoadingPredictions] = React.useState<Record<number, boolean>>({});
    const [predictionErrors, setPredictionErrors] = React.useState<Record<number, string>>({});
    const [batchPredictionInProgress, setBatchPredictionInProgress] = React.useState<boolean>(false);
    const [batchPredictionError, setBatchPredictionError] = React.useState<string>('');

    const teamsDict: Record<string, any> = {}
    teams.map(team => (
      teamsDict[team.name] = team
    ))

    const url = URL.createObjectURL(
      new Blob(
        [JSON.stringify({"Falcons": teamsDict.Falcons})], {type: 'application/json'}
      )
    )

    React.useEffect(() => {
      const fetchData = async () => {
        const response = await fetch(
          `https://site.api.espn.com/apis/site/v2/sports/football/nfl/scoreboard?dates=${year}&seasontype=2&week=${week}`
        );
        const data = await response.json();

        const fetchedItems = data.events.map((event) => {
          return event.competitions[0].competitors.map((competitor) => {
            return {
              label: competitor.team.displayName,
              value: competitor.team.shortDisplayName,
              description: `${competitor.homeAway} team`,
              image: (
                <img
                  src={competitor.team.logo}
                  alt={competitor.team.shortDisplayName}
                  width={50}
                  height={50}
                />
              ),
            }
          });
        });

        // Store game data separately for predictions
        const gameDataForPredictions = data.events.map((event) => ({
          homeTeam: event.competitions[0].competitors.find(c => c.homeAway === 'home')?.team,
          awayTeam: event.competitions[0].competitors.find(c => c.homeAway === 'away')?.team,
          event: event
        }));

        setItems(fetchedItems);
        setGameData(gameDataForPredictions);
        setSelectedItems(new Array(fetchedItems.length).fill("")); // Initialize selectedItems with empty strings
        setPredictions({}); // Reset predictions
        setLoadingPredictions({});
        setPredictionErrors({});
        setLoaded(true);
      };

      fetchData();
      setLoaded(false); // Reset loaded state
    }, [week, year]); // Refetch items when the week changes

    const handleSelectionChange = (index: number, value: string) => {
      setSelectedItems((currentItems) => {
        const updatedItems = [...currentItems];
        updatedItems[index] = value;
        return updatedItems;
      });
    };

    const fetchPrediction = async (gameIndex: number) => {
      if (!gameData[gameIndex]) return;
      
      setLoadingPredictions(prev => ({ ...prev, [gameIndex]: true }));
      setPredictionErrors(prev => ({ ...prev, [gameIndex]: '' }));
      
      try {
        const { homeTeam, awayTeam } = gameData[gameIndex];
        
        if (!homeTeam || !awayTeam) {
          throw new Error('Could not identify home and away teams');
        }
        
        const prediction = await getPredictionForMatchup(homeTeam, awayTeam, teamsDict);
        
        setPredictions(prev => ({ ...prev, [gameIndex]: prediction }));
      } catch (error) {
        console.error('Error fetching prediction:', error);
        setPredictionErrors(prev => ({
          ...prev,
          [gameIndex]: error instanceof Error ? error.message : 'Failed to get prediction'
        }));
      } finally {
        setLoadingPredictions(prev => ({ ...prev, [gameIndex]: false }));
      }
    };

    const fetchAllPredictions = async () => {
      if (!loaded || items.length === 0) return;
      
      setBatchPredictionInProgress(true);
      setBatchPredictionError('');
      
      try {
        for (let i = 0; i < items.length; i++) {
          await fetchPrediction(i);
          // Add small delay between requests to avoid overwhelming the API
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch (error) {
        setBatchPredictionError('Failed to complete all predictions. Some predictions may have failed.');
        console.error('Batch prediction error:', error);
      } finally {
        setBatchPredictionInProgress(false);
      }
    };

    const retryFailedPredictions = async () => {
      if (!loaded || items.length === 0) return;
      
      setBatchPredictionInProgress(true);
      setBatchPredictionError('');
      
      try {
        // Only retry predictions that failed or are missing
        for (let i = 0; i < items.length; i++) {
          if (predictionErrors[i] || !predictions[i]) {
            await fetchPrediction(i);
            // Add small delay between requests
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      } catch (error) {
        setBatchPredictionError('Failed to retry predictions. Please try again.');
        console.error('Retry prediction error:', error);
      } finally {
        setBatchPredictionInProgress(false);
      }
    };

    const clearAllPredictions = () => {
      setPredictions({});
      setPredictionErrors({});
      setLoadingPredictions({});
      setBatchPredictionError('');
    };

    return (
      <Modal
        visible={visible}
        onDismiss={() => setVisible(false)}
        header={`Week ${week} Picks`}
        size="large"
      >
        <SpaceBetween direction="vertical" size="m">
          {loaded && (
            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    <SpaceBetween direction="horizontal" size="xs">
                      <Button
                        variant="normal"
                        onClick={fetchAllPredictions}
                        disabled={batchPredictionInProgress || Object.keys(loadingPredictions).some(key => loadingPredictions[key])}
                        loading={batchPredictionInProgress}
                      >
                        {batchPredictionInProgress ? 'Getting Predictions...' : 'Get AI Predictions'}
                      </Button>
                      <Button
                        variant="normal"
                        onClick={retryFailedPredictions}
                        disabled={batchPredictionInProgress || Object.keys(loadingPredictions).some(key => loadingPredictions[key]) ||
                                 (Object.keys(predictionErrors).length === 0 && Object.keys(predictions).length === items.length)}
                      >
                        Retry Failed
                      </Button>
                      <Button
                        variant="normal"
                        onClick={clearAllPredictions}
                        disabled={batchPredictionInProgress || Object.keys(loadingPredictions).some(key => loadingPredictions[key])}
                      >
                        Clear All
                      </Button>
                    </SpaceBetween>
                  }
                >
                  Matchups
                </Header>
              }
            >
              {batchPredictionError && (
                <Alert type="error" dismissible onDismiss={() => setBatchPredictionError('')}>
                  {batchPredictionError}
                </Alert>
              )}
              <SpaceBetween direction="vertical" size="m">
                {items.map((game, index) => (
                  <Container key={`game-${index}-week-${week}-container`}>
                    <ColumnLayout columns={2} variant="text-grid">
                      <div>
                        <Header variant="h3">Select Winner</Header>
                        <Tiles
                          key={`game-${index}-week-${week}`}
                          columns={2}
                          onChange={({ detail }) => handleSelectionChange(index, detail.value)}
                          value={selectedItems[index]}
                          items={game}
                        />
                      </div>
                      <div>
                        <Header variant="h3">
                          AI Prediction
                          {loadingPredictions[index] && <Spinner size="normal" />}
                        </Header>
                        {loadingPredictions[index] ? (
                          <Box padding="m">
                            <Spinner /> Analyzing matchup...
                          </Box>
                        ) : predictionErrors[index] ? (
                          <Alert type="error" header="Prediction Error">
                            {predictionErrors[index]}
                            <Button
                              variant="link"
                              onClick={() => fetchPrediction(index)}
                            >
                              Retry
                            </Button>
                          </Alert>
                        ) : predictions[index] ? (
                          <Alert
                            type="info"
                            header={`Predicted Winner: ${predictions[index]?.predictedWinner}`}
                          >
                            <SpaceBetween direction="vertical" size="xs">
                              <div>
                                <Badge color="blue">
                                  Confidence: {predictions[index]?.confidence}%
                                </Badge>
                              </div>
                              <div>
                                <strong>Reasoning:</strong> {predictions[index]?.reasoning}
                              </div>
                            </SpaceBetween>
                          </Alert>
                        ) : (
                          <Box padding="m" color="text-body-secondary">
                            <Button
                              variant="link"
                              onClick={() => fetchPrediction(index)}
                            >
                              Get prediction for this matchup
                            </Button>
                          </Box>
                        )}
                      </div>
                    </ColumnLayout>
                  </Container>
                ))}
              </SpaceBetween>
            </Container>
          )}
          <Button
            variant="primary"
            download={'team_data.json'}
            href={url}
          >
            Submit Picks
          </Button>
        </SpaceBetween>
      </Modal>
    );
};